import type { ButtonProps } from 'primevue/button';
import { usePassThrough } from 'primevue/passthrough';
import Tailwind from 'primevue/passthrough/tailwind';

const PrimeTailwind = usePassThrough(
    Tailwind,
    {
        carousel: {
            root: {
                class: ['flex rounded-xl flex-col '],
            },
            content: {
                class: ['flex flex-col overflow-clip relative'],
            },
            nextbuttonicon: {
                class: ['w-4 h-4'],
            },
            previousbuttonicon: {
                class: ['w-4 h-4'],
            },
            container: ({ props }) => ({
                class: [
                    // Flexbox
                    'flex',
                    // Orientation
                    {
                        'flex-row': props.orientation !== 'vertical',
                        'flex-col': props.orientation == 'vertical',
                    },
                ],
            }),
            itemsContent: {
                class: ['overflow-hidden w-full'],
            },
            itemsContainer: ({ props }) => ({
                class: [
                    // Flexbox
                    'flex',
                    'carousel__itemsContainer carousel__mobile-scroll-fix-selector-class rounded-lg',
                    // Orientation & Sizing
                    {
                        'flex-row': props.orientation !== 'vertical',
                        'flex-col h-full': props.orientation == 'vertical',
                    },
                ],
            }),
            item: ({ props }) => ({
                class: [
                    // Flexbox
                    'flex shrink-0 grow justify-center rounded-lg',
                    // Size
                    {
                        'w-full sm:w-[50%] md:w-[33.333333333333336%]': props.orientation !== 'vertical',
                        'w-full h-full': props.orientation == 'vertical',
                    },
                ],
            }),
            itemCloned: ({ props }) => ({
                class: [
                    // Flexbox
                    'flex shrink-0 grow',
                    'unvisible',
                    // Size
                    {
                        'w-full sm:w-[50%] md:w-[33.333333333333336%]': props.orientation !== 'vertical',
                        'w-full h-full': props.orientation == 'vertical',
                    },
                ],
            }),
            indicators: {
                class: ['flex flex-row justify-center flex-wrap absolute bottom-1 left-1/2 -translate-x-1/2'],
            },
            indicator: {
                class: ['mr-2 mb-2'],
            },
            indicatorButton: ({ context }) => ({
                class: [
                    // Sizing & Shape
                    '!w-6 h-[5px] -skew-x-12',
                    // Transitions
                    'transition duration-200',
                    // Focus Styles
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                    // Color & Background
                    {
                        '!bg-black/20 hover:bg-surface-300  ': !context.highlighted,
                        '!bg-primary hover:bg-primary-hover': context.highlighted,
                    },
                ],
            }),
        },

        button: {
            root: ({ props }: { props: ButtonProps }) => {
                const isRejectButton =
                    props.pt && (props.pt as Record<string, any>)['data-pc-section'] === 'rejectbutton';

                const isSecondaryButton = props.pt?.isSecondary;
                const isShadowed = props.pt?.isShadowed;
                const isResized = props.pt?.isResized;
                const isDanger = props.pt?.isDanger;

                const text = isRejectButton
                    ? '!text-black'
                    : isSecondaryButton
                      ? '!text-white text-md'
                      : props.text
                        ? 'text-white text-md'
                        : '!text-white text-md';
                const spacing = props.text ? 'px-3 !py-2' : 'px-6 !py-2';
                const bg = isDanger
                    ? 'bg-red-600'
                    : isRejectButton
                      ? 'bg-surface-900 bg-white'
                      : isShadowed
                        ? 'bg-white shadow-md'
                        : isSecondaryButton
                          ? ' rounded border-none bg-white !bg-black/90 hover:bg-gray-900'
                          : !props.text && '!bg-gradient-to-r from-primary to-secondary border-none';
                const hover = props.text
                    ? 'hover:text-[#ceac54]'
                    : !isRejectButton
                      ? 'hover:brightness-125'
                      : 'hover:bg-gray-50';

                return {
                    class: [
                        text,
                        bg,
                        spacing,
                        hover,
                        isResized ? 'w-fit !px-3' : 'min-w-[100px]',
                        '!font-medium min-h-[38px] justify-center  space-x-1 tracking-tight  !rounded-md',
                        props.disabled && 'opacity-60',
                    ],
                };
            },
            label: '!font-medium',
        },

        confirmpopup: {
            root: {
                class: '!bg-white force-border absolute  rounded-md text-black text-base pb-3',
            },
        },

        chips: {
            root: '!border-gray-400',
            container: '!border-gray-400 rounded-md text-sm',
            input: 'text-sm',
        },

        card: {
            root: 'relative w-full transition-all h-full flex flex-col items-center shadow-lg  !bg-opacity-100 border border-white/10 rounded-lg ',
            caption: 'w-full',
            title: 'w-full !mb-0',
            subtitle: 'w-full !my-4',
            body: 'w-full h-full flex flex-col px-6 ',
            content: '!py-0',
        },
        timeline: {
            content: 'w-full',
            opposite: 'hidden',
            connector: '!bg-white/10 w-0.5 h-full',
        },
        fileupload: {
            buttonbar: 'border-none px-0 py-0',
            empty: 'py-8',
        },
        dropdown: {
            wrapper: '!overflow-auto',
            trigger: 'w-auto ',
            item: 'flex justify-center !py-2 ',
            list: 'space-y-1 px-1',
            input: 'border-none outline-none ring-none !text-sm py-2 !text-black',
            panel: ' border border-white/20 rounded-md  px-2',
            itemLabel: 'text-black text-center !text-sm',
            emptymessage: '!text-sm text-center',
            root: ({ props }: { props: ButtonProps }) => {
                return {
                    class: [
                        'bg-white py-0 border border-gray-400 !opacity-100 appearance-none text-ellipsis overflow-hidden pr-3 outline-none ring-transparent rounded-md  focus:ring-0 ',
                        props.disabled && '!border-gray-200 !bg-gray-50/40 cursor-not-allowed',
                    ],
                };
            },
        },
        multiselect: {
            wrapper: 'overflow-y-scroll',
            filtericon: '!right-4 top-1/2',
            filterinput: '!relative',
            headercheckbox: 'mr-2',
            hiddeninput: '!mr-2 !pr-2 !hidden',
            emptymessage: '!text-sm',
            option: '!ml-2 text-sm',
            list: '!px-2',
            checkbox: 'mr-2',
            itemcheckbox: '!hidden',
            label: '!p-2 !text-sm',
            root: '!border-gray-400 overflow-hidden',
        },
        checkbox: {
            box: '!hidden',
        },
        badge: {
            root: ({ props }) => ({
                class: [
                    // Font
                    'font-medium',
                    'text-xs',
                    // Alignment
                    'flex items-center justify-center !w-fit',
                    'text-center',
                    // Size
                    'm-0',
                    '!px-3 !py-0.5',
                    {
                        'rounded-full': props.nogutter || props.dot,
                        'rounded-[10px]': !props.nogutter && !props.dot,
                    },
                    {
                        'bg-green-500': props.severity === 'success',
                        'bg-primary': props.severity === 'info',
                        'bg-red-500': props.severity === 'danger',
                    },
                    {
                        '!text-white/90':
                            props.severity === 'danger' || props.severity === 'info' || props.severity === 'success',
                    },
                ],
            }),
        },
        skeleton: {
            root: {
                class: 'bg-gray-200 animate-pulse',
            },
        },
        toast: {
            root: ({ props }) => ({
                class: [
                    'w-80 md:w-96 !z-[10000] !opacity-100 rounded-md font-bold',

                    { 'md:-translate-x-2/4': props.position == 'top-center' || props.position == 'bottom-center' },
                ],
            }),
            container: ({ props }) => ({
                class: [
                    'my-3 !rounded-lg w-full',
                    '!border-0',
                    'font-bold',
                    'backdrop-blur-[10px] shadow-md',
                    'text-sm',
                    {
                        '!bg-gray-900': props.message.severity == 'info',
                        'bg-emerald-200/90': props.message.severity == 'success',
                        'bg-red-200/100': props.message.severity == 'warn' || props.message.severity == 'error',
                    },
                    {
                        /* 'border-l-primary': props.message.severity == 'info',*/
                        'border-l-green-500': props.message.severity == 'success',
                        'border-l-red-500': props.message.severity == 'warn' || props.message.severity == 'error',
                    },
                    {
                        '!text-primary': props.message.severity == 'info',
                        'text-green-500': props.message.severity == 'success',
                        'text-red-500': props.message.severity == 'warn' || props.message.severity == 'error',
                    },
                ],
            }),
            content: {
                class: 'flex items-start font-bold p-3',
            },
            /*  icon: {
                class: ['!hidden'],
            }, */
            text: {
                class: ['text-base font-bold leading-none', 'ml-1', 'flex-1'],
            },
            summary: {
                class: 'font-bold',
            },
            detail: {
                class: '!mt-0 block text-sm font-bold',
            },
            closebutton: {
                class: [
                    'flex items-center justify-center',
                    'w-6 h-6',
                    'ml-auto  relative',
                    'rounded-full',
                    'bg-transparent',
                    'transition duration-200 ease-in-out ring-0 focus:ring-0 outline-0 focus:outline-0',
                    'hover:bg-surface-0/50',
                    'overflow-hidden',
                ],
            },
            transition: {
                enterFromClass: 'opacity-0 translate-y-2/4',
                enterActiveClass: 'transition-[transform,opacity] duration-300',
                leaveFromClass: 'max-h-[1000px]',
                leaveActiveClass:
                    '!transition-[max-height_.45s_cubic-bezier(0,1,0,1),opacity_.3s,margin-bottom_.3s] overflow-hidden',
                leaveToClass: 'max-h-0 opacity-0 mb-0',
            },
        },
        inputmask: {
            root: {
                class: 'py-2.5 bg-white/10 border border-white/20 appearance-none text-ellipsis overflow-hidden h-full px-3 text-sm outline-none ring-transparent rounded-md block w-full text-white/90 focus:ring-0  sm:leading-6;',
            },
        },

        menu: {
            root: {
                class: [
                    'min-w-[225px] h-full',
                    'pt-3 mt-3 px-3',
                    '!rounded-none',
                    '!bg-transparent',
                    'text-surface-700',
                    '!border-l-0 !border-t-0 !border-b-0 !border-r border-white/[5%]',
                ],
            },
            menu: {
                class: ['list-none', 'm-0', 'outline-none'],
            },
            menuItem: {
                class: [
                    //Shape
                    'rounded-none my-1',
                    // Colors
                    'text-[15px] font-normal text-white/90 tracking-tight',
                    // Transitions
                    'transition-shadow',
                    'duration-200',
                    // States
                    'hover:text-surface-700',
                    'hover:bg-surface-100',
                ],
            },
            action: {
                class: [
                    'relative',
                    'flex',
                    'items-center',
                    '!py-3',
                    'px-5',
                    'no-underline',
                    'overflow-hidden',
                    'cursor-pointer',
                    'select-none',
                ],
            },
            icon: {
                class: ['mr-2', 'text-surface-600'],
            },
            label: {
                class: ['leading-none'],
            },

            submenuheader: {
                class: [
                    'font-bold',
                    'mt-0',
                    'py-3 px-5',
                    'rounded-tl-none',
                    'rounded-tr-none',
                    'bg-surface-0',
                    'text-surface-700',
                ],
            },
            transition: {
                enterFromClass: 'opacity-0 scale-y-[0.8]',
                enterActiveClass: 'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
                leaveActiveClass: 'transition-opacity duration-100 ease-linear',
                leaveToClass: 'opacity-0',
            },
        },
        dataview: {
            content: {
                class: ['p-0', '!border', 'border-white/10', '!bg-black', '!bg-opacity-75', 'rounded-lg'],
            },
            grid: {
                class: ['flex flex-wrap', 'ml-0 mr-0 mt-0', 'bg-surface-0'],
            },
            header: {
                class: ['!py-3', '!px-6', 'bg-white', 'mb-2', 'rounded-md', 'font-normal', 'border-none'],
            },
            emptymessage: {
                class: ['text-white py-3 px-5'],
            },
        },
        datatable: {
            root: {
                class: ['shadow-lg pt-3 rounded-md bg-white'],
            },
            bodyRow: {
                class: ['!bg-white', 'hover:!bg-gray-100', 'cursor-pointer'],
            },
            loadingOverlay: ['bg-black bg-opacity-70 z-10 rounded-md'],
            column: {
                headercell: ({ context, props }) => ({
                    class: [
                        'font-semibold',
                        'tracking-tight',
                        'text-left',
                        'text-sm',
                        '!border-0',
                        'border-white/10',
                        'px-3 py-1',
                        'text-black',
                        // States
                        {
                            'hover:bg-surface-100 ':
                                (props.sortable === '' || props.sortable) && !(context && context.sorted),
                        },
                        'focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring focus-visible:ring-inset focus-visible:ring-primary-400/50 ',
                        // Transition
                        { 'transition duration-200': props.sortable === '' || props.sortable },
                        // Misc
                        { 'cursor-pointer': props.sortable === '' || props.sortable },
                        {
                            'overflow-hidden space-nowrap  bg-clip-padding': context.resizable,
                            // Resizable
                        },
                    ],
                }),
                headercontent: {
                    class: 'flex items-center',
                },
                sort: ({ context }) => ({
                    class: [context.sorted ? 'text-primary-500' : 'text-surface-700', context.sorted ? '' : ''],
                }),
                bodycell: ({ props, context, parent }) => ({
                    class: [
                        //Position
                        { 'sticky box-border border-b': parent.instance.frozenRow },
                        { 'sticky box-border border-b': props.frozen || props.frozen === '' },
                        // Alignment
                        'text-left',
                        '!bg-transparent',
                        'text-black text-sm tracking-tight',
                        'py-3.5 px-3',
                        // Shape
                        'border-0 border-b border-solid',
                        { 'first:border-l border-r border-b': context == null ? void 0 : context.showGridlines },
                        // Color
                        'border-white/10',
                    ],
                }),
                footercell: ({ context }) => ({
                    class: [
                        // Font
                        'font-bold',
                        // Alignment
                        'text-left',
                        // Shape
                        'border-0 border-b border-solid',
                        { 'border-x border-y': context == null ? void 0 : context.showGridlines },
                        // Spacing
                        (context == null ? void 0 : context.size) === 'small'
                            ? 'p-2'
                            : (context == null ? void 0 : context.size) === 'large'
                              ? 'p-5'
                              : 'p-4',
                        // Color
                        'border-surface-200 ',
                        'text-surface-700 ',
                        'bg-surface-50 ',
                    ],
                }),
                sorticon: ({ context }) => ({
                    class: ['ml-2', context.sorted ? 'text-primary-700 ' : 'text-surface-700 '],
                }),
                sortbadge: {
                    class: [
                        'flex items-center justify-center align-middle',
                        'rounded-full',
                        'w-[1.143rem] leading-[1.143rem]',
                        'ml-2',
                        'text-primary-700 ',
                        'bg-primary-50 ',
                    ],
                },
                columnfilter: {
                    class: 'inline-flex items-center ml-auto !w-full',
                },
                filterinput: {
                    class: '!w-full',
                },
                filteroverlay: {
                    class: [
                        'absolute top-0 left-0',
                        'border-0 ',
                        'rounded-md',
                        'shadow-md',
                        'min-w-[12.5rem]',
                        'bg-surface-0 ',
                        'text-surface-800 ',
                        '',
                    ],
                },
                filtermatchmodedropdown: {
                    root: ({ state }) => ({
                        class: [
                            // Display and Position
                            'flex',
                            'relative',
                            // Spacing
                            'my-2',
                            // Shape
                            'w-full',
                            'rounded-md',
                            // Color and Background
                            'bg-surface-0 ',
                            'border border-surface-300 ',
                            'text-surface-800 ',
                            'placeholder:text-surface-400 ',
                            // Transitions
                            'transition-all',
                            'duration-200',
                            // States
                            'hover:border-primary-500 ',
                            {
                                'outline-none outline-offset-0 ring ring-primary-400/50 ': state.focused,
                            },
                            // Misc
                            'cursor-pointer',
                            'select-none',
                        ],
                    }),
                },
                filterrowitems: {
                    class: 'm-0 p-0 py-3 list-none',
                },
                filterrowitem: ({ context }) => ({
                    class: [
                        // Font
                        'font-normal',
                        'leading-none',
                        // Position
                        'relative',
                        // Shape
                        'border-0',
                        'rounded-none',
                        // Spacing
                        'm-0',
                        'py-3 px-5',
                        // Color
                        { 'text-surface-700 ': !(context != null && context.highlighted) },
                        {
                            'bg-surface-0  text-surface-700 ': !(context != null && context.highlighted),
                        },
                        {
                            'bg-primary-100  text-primary-700 ': context == null ? void 0 : context.highlighted,
                        },
                        {
                            'bg-primary-50  text-primary-700 ': context == null ? void 0 : context.highlighted,
                        },
                        //States
                        {
                            'hover:bg-surface-100 ': !(context != null && context.highlighted),
                        },
                        {
                            'hover:text-surface-700 hover:bg-surface-100  ': !(context != null && context.highlighted),
                        },
                        'focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring focus-visible:ring-inset focus-visible:ring-primary-400/50 ',
                        // Transitions
                        'transition-shadow',
                        'duration-200',
                        // Misc
                        'cursor-pointer',
                        'overflow-hidden',
                        'whitespace-nowrap',
                    ],
                }),
                filteroperator: {
                    class: [
                        'px-5 py-3',
                        'border-b border-solid',
                        'rounded-t-md',
                        'text-surface-700 ',
                        'border-surface-200 ',
                        'bg-surface-0 ',
                    ],
                },
                filteroperatordropdown: {
                    root: ({ state }) => ({
                        class: [
                            // Display and Position
                            'inline-flex',
                            'relative',
                            // Shape
                            'w-full',
                            'rounded-md',
                            // Color and Background
                            'bg-surface-0 ',
                            'border border-surface-300 ',
                            // Transitions
                            'transition-all',
                            'duration-200',
                            // States
                            'hover:border-primary-500 ',
                            {
                                'outline-none outline-offset-0 ring ring-primary-400/50 ': state.focused,
                            },
                            // Misc
                            'cursor-pointer',
                            'select-none',
                        ],
                    }),
                    input: ({ props }) => ({
                        class: [
                            //Font
                            'font-sans',
                            'leading-none',
                            // Display
                            'block',
                            'flex-auto',
                            // Color and Background
                            '!bg-transparent',
                            'border-0',
                            {
                                'text-surface-800 ': props.modelValue,
                                'text-surface-400 ': !props.modelValue,
                            },
                            'placeholder:text-surface-400 ',
                            // Sizing and Spacing
                            'w-[1%]',
                            'p-3',
                            //Shape
                            'rounded-none',
                            // Transitions
                            'transition',
                            'duration-200',
                            // States
                            'focus:outline-none focus:shadow-none',
                            // Misc
                            'relative',
                            'cursor-pointer',
                            'overflow-hidden overflow-ellipsis',
                            'whitespace-nowrap',
                            'appearance-none',
                        ],
                    }),
                    trigger: {
                        class: [
                            'flex items-center justify-center',
                            'shrink-0',
                            'bg-transparent',
                            'text-surface-500',
                            'w-12',
                            'rounded-tr-md',
                            'rounded-br-md',
                        ],
                    },
                    panel: {
                        class: [
                            'absolute top-0 left-0',
                            'border-0 ',
                            'rounded-md',
                            'shadow-md',
                            'bg-surface-0 ',
                            'text-surface-800 ',
                            '',
                        ],
                    },
                    item: ({ context }) => ({
                        class: [
                            // Font
                            'font-normal',
                            'leading-none',
                            // Position
                            'relative',
                            // Shape
                            'border-0',
                            'rounded-none',
                            // Spacing
                            'm-0',
                            'py-3 px-5',
                            // Color
                            { 'text-surface-700 ': !context.focused && !context.selected },
                            {
                                'bg-surface-50  text-surface-700 ': context.focused && !context.selected,
                            },
                            {
                                'bg-primary-100  text-primary-700 ': context.focused && context.selected,
                            },
                            {
                                'bg-primary-50  text-primary-700 ': !context.focused && context.selected,
                            },
                            //States
                            {
                                'hover:bg-surface-100 ': !context.focused && !context.selected,
                            },
                            {
                                'hover:text-surface-700 hover:bg-surface-100  ': context.focused && !context.selected,
                            },
                            // Transitions
                            'transition-shadow',
                            'duration-200',
                            // Misc
                            'cursor-pointer',
                            'overflow-hidden',
                            'whitespace-nowrap',
                        ],
                    }),
                },
                filterconstraint: {
                    class: ['py-3 px-5', 'border-b border-solid', 'border-surface-200 '],
                },
                filteraddrule: {
                    class: 'py-3 px-5',
                },
                filteraddrulebutton: {
                    root: {
                        class: [
                            'relative',
                            'items-center inline-flex text-center align-bottom justify-center',
                            'text-sm py-2 px-3 w-full',
                            'rounded-md',
                            'bg-transparent border-transparent',
                            'text-primary-500 ',
                            'hover:bg-primary-300/20',
                            'transition duration-200 ease-in-out',
                            'cursor-pointer overflow-hidden select-none',
                        ],
                    },
                    label: {
                        class: 'flex-auto grow-0',
                    },
                    icon: {
                        class: 'mr-2',
                    },
                },
                filterremovebutton: {
                    root: {
                        class: [
                            'relative',
                            'items-center inline-flex text-center align-bottom justify-center',
                            'text-sm py-2 px-3 w-full mt-2',
                            'rounded-md',
                            'bg-transparent border-transparent',
                            'text-red-500 ',
                            'hover:bg-red-300/20',
                            'transition duration-200 ease-in-out',
                            'cursor-pointer overflow-hidden select-none',
                        ],
                    },
                    label: {
                        class: 'flex-auto grow-0',
                    },
                    icon: {
                        class: 'mr-2',
                    },
                },
                filterbuttonbar: {
                    class: ['flex items-center justify-between', 'py-3 px-5'],
                },
                filterclearbutton: {
                    root: {
                        class: [
                            'relative',
                            'items-center inline-flex text-center align-bottom justify-center',
                            'text-sm py-2 px-3',
                            'rounded-md',
                            'text-primary-500 border border-primary-500',
                            'hover:bg-primary-300/20',
                            'transition duration-200 ease-in-out',
                            'cursor-pointer overflow-hidden select-none',
                        ],
                    },
                },
                filterapplybutton: {
                    root: {
                        class: [
                            'relative',
                            'items-center inline-flex text-center align-bottom justify-center',
                            'text-sm py-2 px-3',
                            'rounded-md',
                            'text-white ',
                            'bg-primary-500 ',
                            'hover:bg-primary-600  hover:border-primary-600 ',
                            'transition duration-200 ease-in-out',
                            'cursor-pointer overflow-hidden select-none',
                        ],
                    },
                },
                filtermenubutton: ({ context }) => ({
                    class: [
                        '!hidden',
                        // Flex & Alignment
                        'inline-flex items-center justify-center',
                        // Size
                        'w-8 h-8',
                        // Spacing
                        'ml-2',
                        // Shape
                        'rounded-full',
                        // Color
                        { 'bg-primary-50 text-primary-700': context.active },
                        '  ',
                        // States
                        'hover:text-surface-700 hover:bg-surface-300/20',
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                        // Transition
                        'transition duration-200',
                        // Misc
                        'cursor-pointer no-underline overflow-hidden',
                    ],
                }),
                headerfilterclearbutton: ({ context }) => ({
                    class: [
                        'relative',
                        // Flex & Alignment
                        'inline-flex items-center justify-center',
                        'text-left',
                        // Shape
                        'border-none',
                        // Spacing
                        'm-0 p-0 ml-2',
                        // Color
                        'bg-transparent',
                        // Misc
                        'cursor-pointer no-underline overflow-hidden select-none',
                        {
                            invisible: !context.hidden,
                        },
                    ],
                }),
                rowtoggler: {
                    class: [
                        'relative',
                        'inline-flex items-center justify-center',
                        'text-left',
                        'm-0 p-0',
                        'w-8 h-8',
                        'border-0 rounded-full',
                        'text-surface-500 ',
                        'bg-transparent',
                        'focus-visible:outline-none focus-visible:outline-offset-0',
                        'focus-visible:ring focus-visible:ring-primary-400/50 ',
                        'transition duration-200',
                        'overflow-hidden',
                        'cursor-pointer select-none',
                    ],
                },
                columnresizer: {
                    class: [
                        'block',
                        'absolute top-0 right-0',
                        'w-2 h-full',
                        'm-0 p-0',
                        'border border-transparent',
                        'cursor-col-resize',
                    ],
                },
                rowreordericon: {
                    class: 'cursor-move',
                },
                roweditorinitbutton: {
                    class: [
                        'relative',
                        'inline-flex items-center justify-center',
                        'text-left',
                        'w-8 h-8',
                        'border-0 rounded-full',
                        'text-surface-700 ',
                        'border-transparent',
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                        'hover:text-surface-700 hover:bg-surface-300/20',
                        'transition duration-200',
                        'overflow-hidden',
                        'cursor-pointer select-none',
                    ],
                },
                roweditorsavebutton: {
                    class: [
                        'relative',
                        'inline-flex items-center justify-center',
                        'text-left',
                        'w-8 h-8',
                        'border-0 rounded-full',
                        'text-surface-700 ',
                        'border-transparent',
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                        'hover:text-surface-700 hover:bg-surface-300/20',
                        'transition duration-200',
                        'overflow-hidden',
                        'cursor-pointer select-none',
                    ],
                },
                roweditorcancelbutton: {
                    class: [
                        'relative',
                        'inline-flex items-center justify-center',
                        'text-left',
                        'w-8 h-8',
                        'border-0 rounded-full',
                        'text-surface-700 ',
                        'border-transparent',
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                        'hover:text-surface-700 hover:bg-surface-300/20',
                        'transition duration-200',
                        'overflow-hidden',
                        'cursor-pointer select-none',
                    ],
                },
                rowRadiobutton: {
                    root: {
                        class: [
                            'relative',
                            'inline-flex',
                            'align-bottom',
                            'w-[1.571rem] h-[1.571rem]',
                            'cursor-pointer',
                            'select-none',
                        ],
                    },
                    box: ({ props }) => ({
                        class: [
                            // Flexbox
                            'flex justify-center items-center',
                            // Size
                            'w-[1.571rem] h-[1.571rem]',
                            // Shape
                            'border-2',
                            'rounded-full',
                            // Transition
                            'transition duration-200 ease-in-out',
                            // Colors
                            {
                                'text-surface-700 ': !props.modelValue,
                                'bg-surface-0 ': !props.modelValue,
                                'border-surface-300 ': !props.modelValue,
                                'border-primary-500 ': props.modelValue,
                                'bg-primary-500 ': props.modelValue,
                            },
                            // States
                            {
                                'peer-hover:border-primary-500 ': !props.disabled,
                                'peer-hover:border-primary-600  peer-hover:bg-primary-600 ':
                                    !props.disabled && props.modelValue,
                                'peer-focus-visible:border-primary-500  peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400/20 ':
                                    !props.disabled,
                                'opacity-60 cursor-default': props.disabled,
                            },
                        ],
                    }),
                    input: {
                        class: [
                            'peer',
                            'w-full ',
                            'h-full',
                            'absolute',
                            'top-0 left-0',
                            'z-10',
                            'p-0',
                            'm-0',
                            'opacity-0',
                            'rounded-md',
                            'outline-none',
                            'border-2 border-surface-200 ',
                            'appearance-none',
                            'cursor-pointer',
                        ],
                    },
                    icon: ({ props }) => ({
                        class: [
                            'block',
                            // Shape
                            'rounded-full',
                            // Size
                            'w-[0.857rem] h-[0.857rem]',
                            // Colors
                            'bg-surface-0 ',
                            // Conditions
                            {
                                'backface-hidden scale-10 invisible': !props.modelValue,
                                'transform visible scale-[1.1]': props.modelValue,
                            },
                            // Transition
                            'transition duration-200',
                        ],
                    }),
                },
                headercheckbox: {
                    root: {
                        class: [
                            'relative',
                            'inline-flex',
                            'align-bottom',
                            'w-6',
                            'h-6',
                            'cursor-pointer',
                            'select-none',
                        ],
                    },
                    box: ({ props, context }) => ({
                        class: [
                            // Alignment
                            'flex',
                            'items-center',
                            'justify-center',
                            // Size
                            'w-6',
                            'h-6',
                            // Shape
                            'rounded-md',
                            'border-2',
                            // Colors
                            {
                                'border-surface-200 bg-surface-0  ': !context.checked,
                                'border-primary-500 bg-primary-500  ': context.checked,
                            },
                            // States
                            {
                                'peer-hover:border-primary-500 ': !props.disabled && !context.checked,
                                'peer-hover:bg-primary-600  peer-hover:border-primary-700 ':
                                    !props.disabled && context.checked,
                                'peer-focus-visible:border-primary-500  peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400/20 ':
                                    !props.disabled,
                                'cursor-default opacity-60': props.disabled,
                            },
                            // Transitions
                            'transition-colors',
                            'duration-200',
                        ],
                    }),

                    input: {
                        class: [
                            'peer',
                            'w-full ',
                            'h-full',
                            'absolute',
                            'top-0 left-0',
                            'z-10',
                            'p-0',
                            'm-0',
                            'opacity-0',
                            'rounded-md',
                            'outline-none',
                            'border-2 border-surface-200 ',
                            'appearance-none',
                            'cursor-pointer',
                        ],
                    },
                    icon: {
                        class: [
                            'text-base leading-none',
                            'w-4',
                            'h-4',
                            'text-white ',
                            'transition-all',
                            'duration-200',
                        ],
                    },
                },
                rowCheckbox: {
                    root: {
                        class: [
                            'relative',
                            'inline-flex',
                            'align-bottom',
                            'w-6',
                            'h-6',
                            'cursor-pointer',
                            'select-none',
                        ],
                    },
                    box: ({ props, context }) => ({
                        class: [
                            // Alignment
                            'flex',
                            'items-center',
                            'justify-center',
                            // Size
                            'w-6',
                            'h-6',
                            // Shape
                            'rounded-md',

                            'border-2',
                            // Colors
                            {
                                'border-surface-200 bg-surface-0  ': !context.checked,
                                'border-primary-500 bg-primary-500  ': context.checked,
                            },
                            // States
                            {
                                'peer-hover:border-primary-500 ': !props.disabled && !context.checked,
                                'peer-hover:bg-primary-600  peer-hover:border-primary-700 ':
                                    !props.disabled && context.checked,
                                'peer-focus-visible:border-primary-500  peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400/20 ':
                                    !props.disabled,
                                'cursor-default opacity-60': props.disabled,
                            },
                            // Transitions
                            'transition-colors',
                            'duration-200',
                        ],
                    }),
                    input: {
                        class: [
                            'peer',
                            'w-full ',
                            'h-full',
                            'absolute',
                            'top-0 left-0',
                            'z-10',
                            'p-0',
                            'm-0',
                            'opacity-0',
                            'rounded-md',
                            'outline-none',
                            'border-2 border-surface-200 ',
                            'appearance-none',
                            'cursor-pointer',
                        ],
                    },
                    icon: {
                        class: [
                            'text-base leading-none',
                            'w-4',
                            'h-4',
                            'text-white ',
                            'transition-all',
                            'duration-200',
                        ],
                    },
                },
                transition: {
                    enterFromClass: 'opacity-0 scale-y-[0.8]',
                    enterActiveClass: 'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
                    leaveActiveClass: 'transition-opacity duration-100 ease-linear',
                    leaveToClass: 'opacity-0',
                },
            },
            bodyrow: ({ context, props }) => ({
                class: [
                    // Color
                    '',
                    { 'bg-primary-50 text-primary-700 ': context.selected },
                    { 'bg-surface-0 text-surface-600 ': !context.selected },
                    { 'font-bold bg-surface-0 ': props.frozenRow },
                    {
                        'odd:bg-surface-0 odd:text-surface-600  even:bg-surface-50 even:text-surface-600 ':
                            context.stripedRows,
                    },
                    // State
                    {
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ring-inset ':
                            context.selectable,
                    },
                    { 'hover:bg-surface-300/20 hover:text-surface-600': props.selectionMode && !context.selected },
                    // Transition
                    { 'transition duration-200': (props.selectionMode && !context.selected) || props.rowHover },
                    // Misc
                    { 'cursor-pointer': props.selectionMode },
                ],
            }),
            rowexpansion: {
                class: 'bg-surface-0  text-surface-600 ',
            },
            rowgroupheader: {
                class: ['sticky z-20', 'bg-surface-0 text-surface-600 ', ''],
            },
            rowgroupfooter: {
                class: ['sticky z-20', 'bg-surface-0 text-surface-600 ', ''],
            },
            rowgrouptoggler: {
                class: [
                    'relative',
                    'inline-flex items-center justify-center',
                    'text-left',
                    'm-0 p-0',
                    'w-8 h-8',
                    'border-0 rounded-full',
                    'text-surface-500 ',
                    'bg-transparent',
                    'focus-visible:outline-none focus-visible:outline-offset-0',
                    'focus-visible:ring focus-visible:ring-primary-400/50 ',
                    'transition duration-200',
                    'overflow-hidden',
                    'cursor-pointer select-none',
                ],
            },
            rowgrouptogglericon: {
                class: 'inline-block w-4 h-4',
            },
            resizehelper: {
                class: 'absolute hidden w-[2px] z-20 bg-primary-500 ',
            },
            paginatorwrapper: {
                class: ['!bg-transparent'],
            },
        },

        galleria: {
            closebutton: {
                class: '!absolute right-4 top-4',
            },
        },

        paginator: {
            root: {
                class: [
                    'flex gap-4 !bg-white/[5%] text-white/90 items-center justify-center flex-wrap',
                    'px-3 !py-1.5',
                    'border-0',
                    'bg-surface-0 ',
                    'text-surface-500 ',
                ],
            },
            firstpagebutton: ({ context }) => ({
                class: [
                    'relative',
                    // Flex & Alignment
                    'inline-flex items-center justify-center',
                    // Shape
                    'border-0 rounded-full ',
                    'leading-none',
                    // Color
                    'text-surface-500 ',
                    // State
                    {
                        'hover:bg-surface-50 ': !context.disabled,
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ':
                            !context.disabled,
                    },
                    // Transition
                    'transition duration-200',
                    // Misc
                    'user-none overflow-hidden',
                    { 'cursor-default pointer-events-none opacity-60': context.disabled },
                ],
            }),
            previouspagebutton: ({ context }) => ({
                class: [
                    'relative',
                    // Flex & Alignment
                    'inline-flex items-center justify-center',
                    // Shape
                    'border-0 rounded-full ',
                    'leading-none',
                    // Color
                    'text-surface-500 ',
                    // State
                    {
                        'hover:bg-surface-50 ': !context.disabled,
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ':
                            !context.disabled,
                    },
                    // Transition
                    'transition duration-200',
                    // Misc
                    'user-none overflow-hidden',
                    { 'cursor-default pointer-events-none opacity-60': context.disabled },
                ],
            }),
            nextpagebutton: ({ context }) => ({
                class: [
                    'relative',
                    // Flex & Alignment
                    'inline-flex items-center justify-center',
                    // Shape
                    'border-0 rounded-full ',
                    'leading-none',
                    // Color
                    'text-surface-500 ',
                    // State
                    {
                        'hover:bg-surface-50 ': !context.disabled,
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ':
                            !context.disabled,
                    },
                    // Transition
                    'transition duration-200',
                    // Misc
                    'user-none overflow-hidden',
                    { 'cursor-default pointer-events-none opacity-60': context.disabled },
                ],
            }),
            lastpagebutton: ({ context }) => ({
                class: [
                    'relative',
                    // Flex & Alignment
                    'inline-flex items-center justify-center',
                    // Shape
                    'border-0 rounded-full ',
                    'leading-none',
                    // Color
                    'text-surface-500 ',
                    // State
                    {
                        'hover:bg-surface-50 ': !context.disabled,
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ':
                            !context.disabled,
                    },
                    // Transition
                    'transition duration-200',
                    // Misc
                    'user-none overflow-hidden',
                    { 'cursor-default pointer-events-none opacity-60': context.disabled },
                ],
            }),
            pagebutton: ({ context }) => ({
                class: [
                    'relative',
                    '!h-8 !w-8',
                    '!outline-none',
                    'focus:ring-0',
                    // Flex & Alignment
                    'inline-flex items-center justify-center',
                    // Shape
                    'border-0 rounded-full ',
                    // Size
                    'leading-none',
                    // Color
                    'text-white/90',
                    {
                        '!border border-primary font-medium !text-primary': context.active,
                    },
                    // State
                    {
                        'text-black': !context.active,
                        'hover:bg-surface-50  !text-gray-400': !context.disabled && !context.active,
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ':
                            !context.disabled,
                    },
                    // Transition
                    'transition duration-200',
                    // Misc
                    'user-none overflow-hidden',
                    { 'cursor-default pointer-events-none opacity-60': context.disabled },
                ],
            }),
        },
        tabmenu: {
            root: {
                class: 'overflow-x-auto',
            },
            menu: {
                class: [
                    '!bg-transparent transition-all duration-200',
                    'border-b border-b-white/10',
                    'flex flex-1',
                    'list-none',
                    '!p-0 !m-0',
                ],
            },
            action: ({ context, state }) => ({
                class: [
                    'relative !top-0',
                    // Flexbox and Alignment
                    'flex items-center',
                    // Spacing
                    'px-6 py-3',
                    // Shape
                    'border-b',
                    '!bg-transparent',
                    // Colors and Conditions
                    {
                        'border-white/0 !text-black/60 tracking-tight font-medium':
                            state.d_activeIndex !== context.index,
                        '!border-b !border-primary !text-primary tracking-tight font-medium':
                            state.d_activeIndex === context.index,
                    },
                    // States
                    '!focus-visible:outline-none',
                    {
                        'hover:bg-surface-0': state.d_activeIndex !== context.index,
                        'hover:border-surface-100': state.d_activeIndex !== context.index,
                        'hover:text-surface-900': state.d_activeIndex !== context.index,
                    },
                    // Transitions
                    /*    'transition-all duration-200',*/
                    // Misc
                    'cursor-pointer select-none text-decoration-none',
                    'overflow-hidden',
                    'user-select-none',
                ],
            }),
        },

        calendar: {
            root: ({ props }) => ({
                class: [
                    // Display and Position
                    'inline-flex',
                    'max-w-full',
                    'w-full',
                    'relative',
                    // Misc
                    {
                        '!opacity-100  focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-500/50  select-none pointer-events-none outlin cursor-default':
                            props.disabled,
                    },
                ],
            }),
            input: ({}) => {
                return {
                    class: [
                        'flex flex-auto',
                        // Font
                        'font-sans leading-none',
                        // Colors
                        'h-[39px]',
                        'bg-white/10 border border-gray-400',
                        'opacity-100',
                        'text-black text-sm',
                        // Spacing
                        'm-0 p-3',
                        // Shape
                        'appearance-none',
                        'rounded-md',
                        // Transitions
                        'transition-colors',
                        'duration-200',
                        // States
                        'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-500/50 ',
                    ],
                };
            },
            inputicon: {
                class: ['absolute top-[50%] -mt-2', 'text-surface-600 ', 'right-[.75rem]'],
            },
            dropdownbutton: {
                root: {
                    class: [
                        'relative',
                        'items-center inline-flex text-center align-bottom',
                        'rounded-r-md',
                        'px-4 py-3 leading-none',
                        ' ',
                        'bg-primary-500 ',
                        'border border-primary-500 ',
                        'focus:outline-none focus:outline-offset-0 focus:ring',
                        'hover:bg-primary-600  hover:border-primary-600 ',
                        'focus:ring-primary-400/50 ',
                    ],
                },
            },

            panel: ({ props }) => ({
                class: [
                    // Display & Position
                    {
                        absolute: !props.inline,
                        'inline-block': props.inline,
                    },
                    // Size
                    { 'w-auto p-1 ': !props.inline },
                    { 'min-w-[80vw] w-auto p-1 ': props.touchUI },
                    { 'p-1 min-w-full': props.inline },
                    // Shape
                    '!border rounded-lg',
                    {
                        'shadow-md': !props.inline,
                    },
                    // Colors

                    '!border-white/20',
                    //misc
                    { 'overflow-x-auto': props.inline },
                ],
            }),
            datepickerMask: {
                class: ['fixed top-0 left-0 w-full h-full', 'flex items-center justify-center', ' bg-opacity-90'],
            },
            header: {
                class: [
                    'font-semibold',
                    'flex items-center justify-between',
                    'p-2',
                    'm-0',
                    'border-b',
                    'rounded-t-md',

                    'border-white/20',
                ],
            },
            previousbutton: {
                class: [
                    'relative',
                    'inline-flex items-center justify-center',
                    'w-8 h-8',
                    'p-0 m-0',
                    'rounded-full',
                    'text-surface-600 ',
                    'border-0',
                    'bg-transparent',
                    'transition-colors duration-200 ease-in-out',
                    'hover:text-surface-700 ',
                    'hover:bg-surface-100 ',
                    'cursor-pointer overflow-hidden',
                ],
            },
            title: {
                class: ['leading-8', 'mx-auto my-0'],
            },
            monthTitle: {
                class: [
                    'text-base leading-5',
                    'font-semibold',
                    'text-surface-700 ',
                    'transition duration-200',
                    'p-2',
                    'm-0 mr-2',
                    'hover:text-primary-500 ',
                    'cursor-pointer',
                ],
            },
            yearTitle: {
                class: [
                    'text-base leading-5',
                    'font-semibold',
                    'text-surface-700 ',
                    'transition duration-200',
                    'p-2',
                    'm-0',
                    'hover:text-primary-500 ',
                    'cursor-pointer',
                ],
            },
            nextbutton: {
                class: [
                    'relative',
                    'inline-flex items-center justify-center',
                    'w-8 h-8',
                    'p-0 m-0',
                    'rounded-full',
                    'text-surface-600 ',
                    'border-0',
                    'bg-transparent',
                    'transition-colors duration-200 ease-in-out',
                    'hover:text-surface-700 ',
                    'hover:bg-surface-100 ',
                    'cursor-pointer overflow-hidden',
                ],
            },
            table: {
                class: ['text-base leading-none', 'border-collapse', 'w-full', 'm-0 my-2'],
            },
            tableheadercell: {
                class: ['p-0 md:p-2'],
            },
            weekheader: {
                class: ['leading-5', 'text-white/90', 'opacity-60 cursor-default'],
            },
            weeknumber: {
                class: ['text-white/90', 'opacity-60 cursor-default'],
            },
            weekday: {
                class: ['text-white/90'],
            },
            day: {
                class: ['p-0 md:p-1'],
            },
            weeklabelcontainer: ({ context }) => ({
                class: [
                    // Flexbox and Alignment
                    'flex items-center justify-center',
                    'mx-auto',
                    // Shape & Size
                    'w-10 h-10',
                    'rounded-full',
                    'border-transparent border',
                    // Colors
                    {
                        'text-surface-600  bg-transparent': !context.selected && !context.disabled,
                        'text-primary-700 bg-primary-100': context.selected && !context.disabled,
                    },
                    // States
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                    {
                        'hover:bg-surface-100 ': !context.selected && !context.disabled,
                        'hover:bg-primary-200': context.selected && !context.disabled,
                    },
                    {
                        'opacity-60 cursor-default': context.disabled,
                        'cursor-pointer': !context.disabled,
                    },
                ],
            }),
            daylabel: ({ context }) => ({
                class: [
                    // Flexbox and Alignment
                    'flex items-center justify-center',
                    'mx-auto',
                    // Shape & Size
                    'w-10 h-10',
                    'rounded-md',
                    'border',
                    // Colors
                    {
                        'text-primary font-semibold': context.date.today,
                        'text-black bg-transparent': !context.selected && !context.disabled && !context.date.today,
                        'text-primary !border-primary font-semibold': context.selected && !context.disabled,
                    },
                    // States
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                    {
                        'opacity-30 cursor-default': context.disabled,
                        'cursor-pointer': !context.disabled,
                    },
                ],
            }),
            monthpicker: {
                class: ['my-2'],
            },
            month: ({ context }) => ({
                class: [
                    // Flexbox and Alignment
                    'inline-flex items-center justify-center',
                    // Size
                    'w-1/3',
                    'p-2',
                    // Shape
                    'rounded-md',
                    // Colors
                    {
                        'text-surface-600  bg-transparent': !context.selected && !context.disabled,
                        'text-primary-700 bg-primary-100  ': context.selected && !context.disabled,
                    },
                    // States
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                    {
                        'hover:bg-surface-100 ': !context.selected && !context.disabled,
                        'hover:bg-primary-200 ': context.selected && !context.disabled,
                    },
                    // Misc
                    'cursor-pointer',
                ],
            }),
            yearpicker: {
                class: ['my-2'],
            },
            year: ({ context }) => ({
                class: [
                    // Flexbox and Alignment
                    'inline-flex items-center justify-center',
                    // Size
                    'w-1/3',
                    'p-2',
                    // Shape
                    'rounded-md',
                    // Colors
                    {
                        'text-surface-600  bg-transparent': !context.selected && !context.disabled,
                        'text-primary-700 bg-primary-100  ': context.selected && !context.disabled,
                    },
                    // States
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-400/50 ',
                    {
                        'hover:bg-surface-100 ': !context.selected && !context.disabled,
                        'hover:bg-primary-200 ': context.selected && !context.disabled,
                    },
                    // Misc
                    'cursor-pointer',
                ],
            }),
            timepicker: {
                class: ['flex', 'justify-center items-center', 'border-t-1', 'border-solid border-surface-200', 'p-2'],
            },
            separatorcontainer: {
                class: ['flex', 'items-center', 'flex-col', 'px-2'],
            },
            separator: {
                class: ['text-xl'],
            },
            hourpicker: {
                class: ['flex', 'items-center', 'flex-col', 'px-2'],
            },
            minutepicker: {
                class: ['flex', 'items-center', 'flex-col', 'px-2'],
            },
            secondPicker: {
                class: ['flex', 'items-center', 'flex-col', 'px-2'],
            },
            ampmpicker: {
                class: ['flex', 'items-center', 'flex-col', 'px-2'],
            },
            incrementbutton: {
                class: [
                    'relative',
                    'inline-flex items-center justify-center',
                    'w-8 h-8',
                    'p-0 m-0',
                    'rounded-full',
                    'text-surface-600 ',
                    'border-0',
                    'bg-transparent',
                    'transition-colors duration-200 ease-in-out',
                    'hover:text-surface-700 ',
                    'hover:bg-surface-100 ',
                    'cursor-pointer overflow-hidden',
                ],
            },
            decrementbutton: {
                class: [
                    'relative',
                    'inline-flex items-center justify-center',
                    'w-8 h-8',
                    'p-0 m-0',
                    'rounded-full',
                    'text-surface-600 ',
                    'border-0',
                    'bg-transparent',
                    'transition-colors duration-200 ease-in-out',
                    'hover:text-surface-700 ',
                    'hover:bg-surface-100 ',
                    'cursor-pointer overflow-hidden',
                ],
            },
            groupcontainer: {
                class: ['flex'],
            },
            group: {
                class: [
                    'flex-1',
                    'border-l',
                    'border-surface-200',
                    'pr-0.5',
                    'pl-0.5',
                    'pt-0',
                    'pb-0',
                    'first:pl-0',
                    'first:border-l-0',
                ],
            },
            buttonbar: {
                class: ['flex justify-between items-center', 'py-3 px-0', 'border-t border-surface-200 '],
            },
            todaybutton: {
                root: {
                    class: [
                        'inline-flex items-center justify-center',
                        'px-4 py-3 leading-none',
                        'rounded-md',
                        'bg-transparent border-transparent',
                        'text-primary-500 ',
                        'transition-colors duration-200 ease-in-out',
                        'focus:outline-none focus:outline-offset-0 focus:ring',
                        'hover:bg-primary-300/20',
                        'cursor-pointer',
                    ],
                },
            },
            clearbutton: {
                root: {
                    class: [
                        'inline-flex items-center justify-center',
                        'px-4 py-3 leading-none',
                        'rounded-md',
                        'bg-transparent border-transparent',
                        'text-primary-500 ',
                        'transition-colors duration-200 ease-in-out',
                        'focus:outline-none focus:outline-offset-0 focus:ring',
                        'hover:bg-primary-300/20',
                        'cursor-pointer',
                    ],
                },
            },
            transition: {
                enterFromClass: 'opacity-0 scale-y-[0.8]',
                enterActiveClass: 'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
                leaveActiveClass: 'transition-opacity duration-100 ease-linear',
                leaveToClass: 'opacity-0',
            },
        },
        steps: {
            root: {
                class: ['overflow-x-scroll md:overflow-hidden'],
            },
            menu: {
                class: ['space-x-3 w-fit sm:w-full md:overflow-hidden  md:max-w-auto'],
            },
            menuitem: ({ context, props }) => {
                return context.index === 0
                    ? { class: ['whitespace-nowrap w-full  flex !justify-start'] }
                    : context.index === props.model?.length - 1
                      ? { class: ['whitespace-nowrap flex !justify-end'] }
                      : { class: ['whitespace-nowrap flex !justify-center'] };
            },
        },
        dialog: {
            root: ({ state }) => ({
                class: [
                    // Shape
                    'rounded-lg',
                    'shadow-lg',
                    'border-0',
                    'min-h-40',
                    'bg-white',
                    'p-4',
                    '!min-w-[25rem] md:!min-w-[32rem] max-w-[56rem]',

                    // Size
                    'max-h-[90vh]',
                    'md:m-4',
                    // Transitions
                    'transform',
                    'transition-all',
                    'duration-1000',
                    'scale-100',
                    // Maximized State
                    {
                        'transition-none': state.maximized,
                        'transform-none': state.maximized,
                        '!w-screen': state.maximized,
                        '!h-screen': state.maximized,
                        '!max-h-full': state.maximized,
                        '!top-0': state.maximized,
                        '!left-0': state.maximized,
                    },
                ],
            }),

            header: {
                class: [
                    'flex w-full justify-end items-center !pt-2 h-full pl-6 pr-12 md:pr-4 mb-3 !py-0 text-md tracking-normal font-bold font-peanut',
                    'shrink-0',
                    'border-t-0',
                    'bg-white',
                    'rounded-tl-lg',
                    'rounded-tr-lg',
                    '',
                    'text-surface-700 ',
                ],
            },
            icons: {
                class: ['flex items-center'],
            },
            closeButton: {
                class: [
                    ' !top-[0px] right-0',
                    ' items-center justify-center',
                    'mr-2 pt-0',
                    'last:mr-0',
                    'w-8 h-8',
                    'border-0',
                    'rounded-full',
                    'text-black text-opacity-90',
                    'bg-transparent',
                    'transition duration-200 ease-in-out',
                    '!hover:text-primary ',
                    'hover:bg-surface-100 ',
                    '!focus:outline-none focus:outline-offset-0 focus:!ring-0 focus:ring-inset',
                    'overflow-hidden',
                ],
            },
            maximizablebutton: {
                class: [
                    'relative',
                    'flex items-center justify-center',
                    'mr-2',
                    'last:mr-0',
                    'w-8 h-8',
                    'border-0',
                    'rounded-full',
                    'text-surface-500',
                    'bg-transparent',
                    'transition duration-200 ease-in-out',
                    'hover:text-surface-700 ',
                    'hover:bg-surface-100 ',
                    'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-inset',
                    'focus:ring-primary-400/50 ',
                    'overflow-hidden',
                ],
            },
            closeButtonIcon: {
                class: ['inline-block', 'w-4', 'h-4'],
            },
            maximizableicon: {
                class: ['inline-block', 'w-4', 'h-4'],
            },
            content: ({ state, instance }) => ({
                class: [
                    // Spacing
                    'px-6',
                    'pb-2',
                    'pt-0',
                    'rounded-lg',
                    'h-full',
                    // Shape
                    {
                        grow: state.maximized,
                        'rounded-bl-lg': !instance.$slots.footer,
                        'rounded-br-lg': !instance.$slots.footer,
                    },
                    // Colors
                    'bg-white ',
                    'text-surface-700 ',
                    // Misc
                    'overflow-y-auto',
                ],
            }),

            footer: {
                class: [
                    'flex items-center justify-end',
                    'shrink-0',
                    'text-right',
                    'gap-2',
                    'px-6',
                    'pb-6',
                    'border-t-0',
                    'rounded-b-lg',
                    '!bg-gray-100 ',
                    'text-surface-700 ',
                ],
            },
            mask: ({ props }) => ({
                class: [
                    // Transitions
                    'transition-all',
                    'duration-300',
                    { 'p-5': !props.position == 'full' },
                    // Background and Effects
                    {
                        'has-[.mask-active]:bg-transparent bg-black/40': props.modal,
                        'has-[.mask-active]:backdrop-blur-none backdrop-blur-sm': props.modal,
                    },
                ],
            }),
            transition: ({ props }) =>
                props.position === 'top'
                    ? {
                          enterFromClass:
                              'opacity-0 scale-75 translate-x-0 -translate-y-full translate-z-0 mask-active',
                          enterActiveClass: 'transition-all duration-200 ease-out',
                          leaveActiveClass: 'transition-all duration-200 ease-out',
                          leaveToClass: 'opacity-0 scale-75 translate-x-0 -translate-y-full translate-z-0 mask-active',
                      }
                    : props.position === 'bottom'
                      ? {
                            enterFromClass: 'opacity-0 scale-75 translate-y-full mask-active',
                            enterActiveClass: 'transition-all duration-200 ease-out',
                            leaveActiveClass: 'transition-all duration-200 ease-out',
                            leaveToClass: 'opacity-0 scale-75 translate-x-0 translate-y-full translate-z-0 mask-active',
                        }
                      : props.position === 'left' || props.position === 'topleft' || props.position === 'bottomleft'
                        ? {
                              enterFromClass:
                                  'opacity-0 scale-75 -translate-x-full translate-y-0 translate-z-0 mask-active',
                              enterActiveClass: 'transition-all duration-200 ease-out',
                              leaveActiveClass: 'transition-all duration-200 ease-out',
                              leaveToClass:
                                  'opacity-0 scale-75  -translate-x-full translate-y-0 translate-z-0 mask-active',
                          }
                        : props.position === 'right' ||
                            props.position === 'topright' ||
                            props.position === 'bottomright'
                          ? {
                                enterFromClass:
                                    'opacity-0 scale-75 translate-x-full translate-y-0 translate-z-0 mask-active',
                                enterActiveClass: 'transition-all duration-200 ease-out',
                                leaveActiveClass: 'transition-all duration-200 ease-out',
                                leaveToClass:
                                    'opacity-0 scale-75 translate-x-full translate-y-0 translate-z-0 mask-active',
                            }
                          : {
                                enterFromClass: 'opacity-0 scale-75 mask-active',
                                enterActiveClass: 'transition-all duration-200 ease-out',
                                leaveActiveClass: 'transition-all duration-200 ease-out',
                                leaveToClass: 'opacity-0 scale-75 mask-active',
                            },
        },
        inputgroup: {
            root: ({}) => ({
                class: [
                    '  bg-white/10 flex items-center border border-white/20 appearance-none text-ellipsis overflow-hidden h-full  text-sm outline-none ring-transparent rounded-md block w-full text-white/90 focus:ring-0  sm:leading-6',
                ],
            }),
        },

        inputgroupaddon: {
            root: ({}) => ({
                class: [' px-2'],
            }),
        },
        progressbar: {
            root: {
                class: ['overflow-hidden', 'relative', '!rounded-full', 'border-0', 'h-1', 'w-full', 'bg-white/20'],
            },
            value: ({ props }) => ({
                class: [
                    // Flexbox & Overflow & Position
                    { 'absolute flex items-center justify-center overflow-hidden': props.mode !== 'indeterminate' },
                    // Colors
                    'bg-primary ',
                    // Spacing & Sizing
                    'm-0',
                    { 'h-full w-0': props.mode !== 'indeterminate' },
                    // Shape
                    'border-0',
                    // Transitions
                    {
                        'transition-width duration-1000 ease-in-out': props.mode !== 'indeterminate',
                        'progressbar-value-animate': props.mode == 'indeterminate',
                    },
                    // Before & After (indeterminate)
                    {
                        'before:absolute before:top-0 before:left-0 before:bottom-0 before:bg-inherit ':
                            props.mode == 'indeterminate',
                        'after:absolute after:top-0 after:left-0 after:bottom-0 after:bg-inherit after:delay-1000':
                            props.mode == 'indeterminate',
                    },
                ],
            }),
            label: {
                class: ['inline-flex', 'text-black font-bold py-1 text-sm ', 'leading-6'],
            },
        },
        label: {},
        breadcrumb: {
            root: 'py-1',
            menu: '!space-x-2',
        },
    },

    { mergeProps: true },
);

export default PrimeTailwind;
